import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import SEO from "@/components/common/SEO";
import { api, SITE_URL } from "@/lib/api";

export default function Blog() {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [category, setCategory] = useState(null);

    useEffect(() => {
        setLoading(true);
        const params = category ? `?category=${encodeURIComponent(category)}` : "";
        api.get(`/blog${params}`)
            .then(({ data }) => setPosts(data))
            .catch(() => setPosts([]))
            .finally(() => setLoading(false));
    }, [category]);

    const categories = Array.from(new Set(posts.map((p) => p.category)));

    const blogJsonLd = {
        "@context": "https://schema.org",
        "@type": "Blog",
        name: "Archivo de Cicatrices · NiTanAdicto",
        description:
            "Archivo editorial sobre adicción, recuperación, antipoesía y autoengaño. Por Nelson González (@nitanadicto).",
        url: `${SITE_URL}/blog`,
        blogPost: posts.slice(0, 10).map((p) => ({
            "@type": "BlogPosting",
            headline: p.title,
            datePublished: p.created_at,
            url: `${SITE_URL}/blog/${p.slug}`,
            author: { "@type": "Person", name: "Nelson González" },
        })),
    };

    return (
        <>
            <SEO
                title="Archivo de Cicatrices · Blog"
                description="Entradas, glosario, currículum del autoengaño, mensajes iniciáticos y textos que convierten la vergüenza en espejo."
                path="/blog"
                keywords="blog adicción, NiTanAdicto, Nelson González, recuperación, antipoesía, glosario adicto, Chile"
                jsonLd={blogJsonLd}
            />

            <section className="grain relative py-20 md:py-28 border-b border-[var(--line)]">
                <div className="container-x">
                    <p className="eyebrow mb-6">Archivo público · 09</p>
                    <h1 className="font-display text-6xl md:text-8xl lg:text-9xl leading-[0.85] tracking-tight mb-6">
                        Archivo de{" "}
                        <span className="text-[var(--danger)]">Cicatrices</span>
                    </h1>
                    <p className="font-mono text-base md:text-lg text-[var(--paper)] max-w-2xl leading-relaxed">
                        Entradas, glosario, currículum del autoengaño, mensajes
                        iniciáticos y textos que convierten la vergüenza en
                        espejo.
                    </p>
                </div>
            </section>

            <section className="py-16 md:py-24" data-testid="blog-list">
                <div className="container-x">
                    {/* Category filter */}
                    {categories.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-12">
                            <button
                                onClick={() => setCategory(null)}
                                className={`font-mono text-[11px] uppercase tracking-[0.2em] px-4 py-2 border ${!category ? "bg-[var(--paper-2)] text-black border-[var(--paper-2)]" : "border-[var(--line)] text-[var(--paper)] hover:border-[var(--danger)]"}`}
                                data-testid="blog-filter-all"
                            >
                                Todas
                            </button>
                            {categories.map((c) => (
                                <button
                                    key={c}
                                    onClick={() => setCategory(c)}
                                    className={`font-mono text-[11px] uppercase tracking-[0.2em] px-4 py-2 border ${category === c ? "bg-[var(--danger)] text-[var(--paper-2)] border-[var(--danger)]" : "border-[var(--line)] text-[var(--paper)] hover:border-[var(--danger)]"}`}
                                    data-testid={`blog-filter-${c.toLowerCase()}`}
                                >
                                    {c}
                                </button>
                            ))}
                        </div>
                    )}

                    {loading ? (
                        <p className="font-mono text-sm text-[var(--ink-muted)] text-center py-20">
                            Cargando expedientes…
                        </p>
                    ) : posts.length === 0 ? (
                        <p className="font-mono text-sm text-[var(--ink-muted)] text-center py-20">
                            Sin entradas en este archivo.
                        </p>
                    ) : (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                            {posts.map((post, i) => (
                                <Link
                                    key={post.slug}
                                    to={`/blog/${post.slug}`}
                                    className="dossier hover:border-[var(--danger)] transition-colors group block"
                                    data-testid={`blog-card-${i}`}
                                >
                                    <div className="dossier-header">
                                        <span>{post.category}</span>
                                        <span>
                                            {new Date(post.created_at).toLocaleDateString(
                                                "es-CL",
                                                {
                                                    year: "numeric",
                                                    month: "short",
                                                    day: "2-digit",
                                                },
                                            )}
                                        </span>
                                    </div>
                                    {post.cover_image && (
                                        <div className="aspect-[16/9] w-full overflow-hidden bg-black border-b border-[var(--line)]">
                                            <img
                                                src={
                                                    post.cover_image.startsWith("http")
                                                        ? post.cover_image
                                                        : post.cover_image
                                                }
                                                alt={`Portada del artículo: ${post.title}`}
                                                className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                                                loading="lazy"
                                            />
                                        </div>
                                    )}
                                    <div className="p-6 md:p-7">
                                        <h2 className="font-display text-2xl md:text-3xl mb-3 text-[var(--paper-2)] group-hover:text-[var(--danger)] transition-colors leading-tight">
                                            {post.title}
                                        </h2>
                                        <p className="font-mono text-sm text-[var(--ink-muted)] leading-relaxed line-clamp-4 mb-6">
                                            {post.excerpt}
                                        </p>
                                        <span className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-[var(--paper-2)] group-hover:text-[var(--danger)]">
                                            Leer entrada
                                            <ArrowUpRight className="w-3.5 h-3.5" />
                                        </span>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </>
    );
}
